import React, { useState, useRef, useEffect, useContext } from "react";
import { FaLock } from "react-icons/fa";
import { X } from "lucide-react";
import { sendOtp, verifyOtp } from "../../services/SignIn/authApi";
import { UserContext } from "../../context/UserContext";
import { requestPermission } from "../../firebase/messaging";
import { toast } from "react-toastify";
import { usePopupLock } from "../../context/PopupLockContext";

export default function LoginPopup({ isOpen, onClose, disabled }) {
  const { loginUser } = useContext(UserContext);
  const { openLock, closeLock } = usePopupLock();

  const [step, setStep] = useState("choice");
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [error, setError] = useState("");
  
  // Stronger guards
  const [isClosing, setIsClosing] = useState(false);
  const hasOpenedRef = useRef(false);
  const closeTimeoutRef = useRef(null);
  const openTimeRef = useRef(null); // ✅ NEW - ADD THIS LINE

  const firstOtpRef = useRef(null);
  const popupRef = useRef(null);

  // Popup lock management
  useEffect(() => {
    if (isOpen && !disabled && !isClosing) {
      openLock("login");
      console.log("🔒 Login popup locked");
    } else {
      closeLock("login");
      console.log("🔓 Login popup unlocked");
    }
    
    return () => {
      closeLock("login");
    };
  }, [isOpen, disabled, isClosing, openLock, closeLock]);

  // ✅ 5 MINUTE AUTO-CLOSE TIMER
  useEffect(() => {
    if (isOpen && !disabled && !isClosing) {
      openTimeRef.current = Date.now();
      console.log("🔓 Popup opened at:", new Date().toLocaleTimeString());
      
      const autoCloseTimer = setTimeout(() => {
        console.log("⏱️ 5 minutes completed. Auto-closing popup...");
        handleSafeClose();
      }, 300000); // 5 * 60 * 1000
      
      return () => clearTimeout(autoCloseTimer);
    }
  }, [isOpen, disabled, isClosing]);

  // RESET WHEN OPEN - with stronger guard
  useEffect(() => {
    if (isOpen && !disabled && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      setStep("choice");
      setIdentifier("");
      setOtp(["", "", "", "", "", ""]);
      setError("");
      setTimeLeft(0);
      setIsClosing(false);
      
      setTimeout(() => {
        hasOpenedRef.current = false;
      }, 300);
    }
    
    if (!isOpen) {
      hasOpenedRef.current = false;
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    }
  }, [isOpen, disabled]);

  // AUTO CLOSE IF DISABLED
  useEffect(() => {
    if (disabled && isOpen && !isClosing) {
      handleSafeClose();
    }
  }, [disabled, isOpen, isClosing]);

  // ESC CLOSE
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && isOpen && !isClosing && !loading) {
        handleSafeClose();
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, isClosing, loading]);

  // CLICK OUTSIDE CLOSE
  const handleOutsideClick = (e) => {
    if (disabled || isClosing || loading) return;

    if (popupRef.current && !popupRef.current.contains(e.target)) {
      handleSafeClose();
    }
  };

  // AUTO FOCUS OTP
  useEffect(() => {
    if (step === "otp" && isOpen && !isClosing) {
      setTimeout(() => {
        firstOtpRef.current?.focus();
      }, 100);
    }
  }, [step, isOpen, isClosing]);

  // TIMER
  useEffect(() => {
    if (!timeLeft) return;
    const t = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft]);

  // ✅ UPDATED - SAFE CLOSE FUNCTION with duration tracking
  const handleSafeClose = () => {
    if (isClosing || !isOpen) return;
    
    // Duration calculate karein
    if (openTimeRef.current) {
      const duration = (Date.now() - openTimeRef.current) / 1000;
      const minutes = Math.floor(duration / 60);
      const seconds = Math.floor(duration % 60);
      console.log(`⏱️ Popup was open for: ${minutes}m ${seconds}s`);
    }
    
    setIsClosing(true);
    
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    
    closeTimeoutRef.current = setTimeout(() => {
      onClose();
      setTimeout(() => {
        setIsClosing(false);
        hasOpenedRef.current = false;
      }, 100);
    }, 50);
  };

  // BLOCK RENDER
  if (!isOpen || disabled || isClosing) return null;

  // SEND OTP
  const handleSendOtp = async () => {
    if (!identifier) {
      setError("Enter mobile or email");
      return;
    }

    setLoading(true);
    setError("");
    const res = await sendOtp(identifier);
    setLoading(false);

    if (res?.success) {
      setStep("otp");
      setTimeLeft(30);
      setOtp(["", "", "", "", "", ""]);
      toast.success("OTP Sent");
    } else {
      setError(res?.error || "Failed to send OTP");
    }
  };

  // VERIFY OTP
  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");

    if (enteredOtp.length !== 6) {
      setError("Enter complete OTP");
      return;
    }

    setLoading(true);
    const res = await verifyOtp(identifier, enteredOtp);
    setLoading(false);

    if (res?.success) {
      const fcmToken = await requestPermission();

      loginUser({
        userId: res.user?.id,
        identifier,
        token: res.token,
        fcmToken,
      });

      toast.success("Login Successful ✅");
      handleSafeClose();
    } else {
      setError(res?.error || "Invalid OTP");
    }
  };

  // OTP INPUT
  const handleOtpChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
    if (!value && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  return (
    <div
      onClick={handleOutsideClick}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
    >
      <div
        ref={popupRef}
        className="relative w-[90%] max-w-sm bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl p-6 text-center animate-scaleIn"
      >
        <button
          onClick={handleSafeClose}
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-md hover:bg-red-100 hover:text-red-600 transition-all duration-200"
        >
          <X size={18} />
        </button>

        <div className="flex justify-center mb-4">
          <div className="bg-red-100 text-red-600 p-4 rounded-full shadow-md">
            <FaLock size={20} />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Login Required
        </h2>

        <p className="text-gray-500 text-sm mb-6">
          Please login to continue
        </p>

        {step === "choice" && (
          <div className="flex flex-col gap-3">
            <button
              onClick={() => setStep("form")}
              className="w-full py-2.5 rounded-lg bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold shadow-md hover:opacity-90 active:scale-95 transition"
            >
              Login
            </button>

            <button
              onClick={handleSafeClose}
              className="w-full py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
          </div>
        )}

        {step === "form" && (
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Enter Mobile or Email"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="w-full border px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            />

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <button
              onClick={handleSendOtp}
              disabled={loading}
              className="w-full py-2.5 rounded-lg bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold shadow-md hover:opacity-90 active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Sending..." : "Send OTP"}
            </button>
          </div>
        )}

        {step === "otp" && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              OTP sent to <b>{identifier}</b>
            </p>

            <div className="flex justify-between gap-2">
              {otp.map((d, i) => (
                <input
                  key={i}
                  id={`otp-${i}`}
                  ref={i === 0 ? firstOtpRef : null}
                  value={d}
                  onChange={(e) => handleOtpChange(e.target.value, i)}
                  maxLength={1}
                  className="w-10 h-12 text-center border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              ))}
            </div>

            {timeLeft > 0 ? (
              <p className="text-xs text-gray-500">
                Resend in {timeLeft}s
              </p>
            ) : (
              <button onClick={handleSendOtp} className="text-red-500 text-sm hover:text-red-600 transition">
                Resend OTP
              </button>
            )}

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <button
              onClick={handleVerifyOtp}
              disabled={loading}
              className="w-full py-2.5 rounded-lg bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold shadow-md hover:opacity-90 active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}